import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { PhpCategory, PhpCategoryCatalog, PhpCategoryService } from 'src/app/services/php-category.service';
import { UtilService } from 'src/app/services/util.service';
import { SkeletonComponent } from '../skeleton/skeleton.component';
import { SwDrawerComponent } from '../sw-drawer/sw-drawer.component';
import { SwSelectComponent } from '../sw-select/sw-select.component';

/** What the edit drawer holds while a category is being changed. */
interface CategoryDraft {
  name: string;
  sort_order: number;
  subtitle: string;
  icon: string;
  colour: string;
  asks_tenant: boolean;
  asks_taken: boolean;
  asks_action: boolean;
  asks_response: boolean;
  tracks_violation: boolean;
  events: string[];
  evout: Record<string, string[]>;
  questions: string[];
  hows: string[];
  acts: string[];
  resps: string[];
  newevents: string[];
  evnew: Record<string, string[]>;
  newhows: string[];
  newacts: string[];
  newresps: string[];
  newQuestions: { label: string; answers: string }[];
}

/**
 * The client portal's incident categories, listed under the Dynamic Form page's own forms and
 * editable here. Saving calls the portal's API, which runs the same checks as the portal's own
 * "Incident categories" form and writes to the portal's database — nothing is stored in this app.
 */
@Component({
  selector: 'app-php-category-section',
  imports: [CommonModule, FormsModule, SkeletonComponent, SwDrawerComponent, SwSelectComponent],
  templateUrl: './php-category-section.component.html',
  styleUrl: './php-category-section.component.scss'
})
export class PhpCategorySectionComponent implements OnInit {
  private service = inject(PhpCategoryService);
  private _utils = inject(UtilService);
  private sanitizer = inject(DomSanitizer);

  enabled = this.service.enabled;
  isLoading = false;
  loadFailed = false;
  categories: PhpCategory[] = [];
  // icon key -> drawing, sent with the list so the cards can show them.
  iconMap: Record<string, string> = {};

  panelOpen = false;
  isOpening: number | null = null;
  isOpeningNew = false;
  isDeleting: number | null = null;
  isSaving = false;
  editing: PhpCategory | null = null;
  catalog: PhpCategoryCatalog | null = null;
  draft: CategoryDraft | null = null;

  // The "Add option" boxes; each is cleared when its option is added.
  newEventText = '';
  newHowText = '';
  newActText = '';
  newRespText = '';
  newResultText: Record<string, string> = {};
  newQuestionLabel = '';
  newQuestionAnswers = '';

  ngOnInit(): void {
    if (this.enabled) {
      this.load();
    }
  }

  load(): void {
    this.isLoading = true;
    this.loadFailed = false;
    this.service.list().subscribe({
      next: (res) => {
        this.categories = res?.categories || [];
        this.iconMap = res?.icons || {};
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.loadFailed = true;
      },
    });
  }

  // ---- list ---------------------------------------------------------------------------------

  describe(c: PhpCategory): string {
    const ev = c.events.length;
    const rs = c.outcomes.length;
    return `${ev} “what happened” option${ev === 1 ? '' : 's'} · ${rs} result${rs === 1 ? '' : 's'}`;
  }

  /**
   * An icon's markup comes from the portal over the network, so only plain drawing shapes are
   * let through — anything else (scripts, handlers, other elements) and the icon is left out.
   */
  icon(svg: string | undefined): SafeHtml {
    if (!svg || !/^(?:<(?:path|circle|rect|line|polyline|polygon|ellipse|g)\b[^<>]*\/?>|<\/g>|\s)+$/i.test(svg)
      || /on\w+\s*=|javascript:/i.test(svg)) {
      return '';
    }
    return this.sanitizer.bypassSecurityTrustHtml(
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" '
      + 'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + svg + '</svg>');
  }

  // ---- edit ---------------------------------------------------------------------------------

  openEdit(c: PhpCategory): void {
    if (this.isOpening !== null) {
      return;
    }
    this.isOpening = c.id;
    this.service.get(c.id).subscribe({
      next: (res) => {
        this.isOpening = null;
        this.editing = res.category;
        this.catalog = res.catalog;
        this.draft = this.toDraft(res.category, res.catalog);
        this.resetAddBoxes();
        this.panelOpen = true;
      },
      error: (e) => {
        this.isOpening = null;
        this._utils.showErrorInfo('', e?.error?.error || 'Could not open that category.');
      },
    });
  }

  /**
   * Delete: asks first, saying how many reports go with the category, then deletes it in the portal.
   * The count shown is sent back with the request, so if reports were filed in the meantime the
   * portal refuses and nothing is lost that the person did not see.
   */
  deleteCategory(c: PhpCategory): void {
    if (this.isDeleting !== null) {
      return;
    }
    const reports = c.reports;
    const consequence = reports
      ? `This also permanently deletes the ${reports} incident report${reports === 1 ? '' : 's'} filed under it, with their footage records, tenant units and answers. This cannot be undone.`
      : 'Nothing is filed under it, so no reports are lost. This cannot be undone.';
    this._utils.confirmationAlert('Delete category', `Delete “${c.name}”? ${consequence}`, (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }
      this.isDeleting = c.id;
      this.service.delete(c.id, reports).subscribe({
        next: (res) => {
          this.isDeleting = null;
          this._utils.showErrorSuccess('', res?.message || 'Category deleted.');
          this.load();
        },
        error: (e) => {
          this.isDeleting = null;
          this._utils.showErrorInfo('', e?.error?.error || 'Could not delete the category.');
          // A changed count (or an already-deleted category): show the list as it is now.
          this.load();
        },
      });
    });
  }

  /** True while the drawer holds a category that has not been added yet. */
  get isNew(): boolean {
    return this.editing?.id === 0;
  }

  /** Add category: a blank category in the same editor. */
  openCreate(): void {
    if (this.isOpeningNew || this.isOpening !== null) {
      return;
    }
    this.isOpeningNew = true;
    this.service.getNew().subscribe({
      next: (res) => {
        this.isOpeningNew = false;
        this.editing = res.category;
        this.catalog = res.catalog;
        this.draft = this.toDraft(res.category, res.catalog);
        this.resetAddBoxes();
        this.panelOpen = true;
      },
      error: (e) => {
        this.isOpeningNew = false;
        this._utils.showErrorInfo('', e?.error?.error || 'Could not open the form.');
      },
    });
  }

  closePanel(): void {
    this.panelOpen = false;
    this.editing = null;
    this.draft = null;
  }

  private resetAddBoxes(): void {
    this.newEventText = '';
    this.newHowText = '';
    this.newActText = '';
    this.newRespText = '';
    this.newResultText = {};
    this.newQuestionLabel = '';
    this.newQuestionAnswers = '';
  }

  private toDraft(c: PhpCategory, cat: PhpCategoryCatalog): CategoryDraft {
    const evout: Record<string, string[]> = {};
    for (const ev of c.events) {
      evout[ev] = [...(c.event_outcomes?.[ev] || [])];
    }
    return {
      name: c.name,
      sort_order: c.sort_order,
      subtitle: c.subtitle,
      icon: c.icon,
      colour: c.colour,
      asks_tenant: c.asks_tenant,
      asks_taken: c.asks_taken,
      asks_action: c.asks_action,
      asks_response: c.asks_response,
      tracks_violation: c.tracks_violation,
      events: [...c.events],
      evout,
      questions: [...c.questions],
      // The three shared lists start from what is on the form right now.
      hows: cat.hows.filter((o) => o.offered).map((o) => o.code),
      acts: cat.actions.filter((o) => o.offered).map((o) => o.code),
      resps: cat.responses.filter((o) => o.offered).map((o) => o.code),
      newevents: [],
      evnew: {},
      newhows: [],
      newacts: [],
      newresps: [],
      newQuestions: [],
    };
  }

  /** Whether this category is the one that asks "How he gained access". */
  get asksHow(): boolean {
    return this.editing?.code === 'unauth';
  }

  /** The drawing for an icon key, from the options the portal sent for this edit. */
  iconSvgOf(key: string): string | undefined {
    return this.catalog?.icons.find((i) => i.key === key)?.svg;
  }

  /** Picking or clearing a "what happened" answer also adds or drops its results line. */
  onEventsChange(codes: string[]): void {
    if (!this.draft) {
      return;
    }
    this.draft.events = [...(codes || [])];
    const evout: Record<string, string[]> = {};
    for (const code of this.draft.events) {
      evout[code] = this.draft.evout[code] || [];
    }
    for (const code of Object.keys(this.draft.evnew)) {
      if (!this.draft.events.includes(code)) {
        delete this.draft.evnew[code];
      }
    }
    this.draft.evout = evout;
  }

  eventName(code: string): string {
    return this.catalog?.events.find((e) => e.code === code)?.name || code;
  }

  /** The text typed into an "Add option" box, trimmed, or '' when there is nothing to add. */
  private take(text: string): string {
    return (text || '').trim();
  }

  addEvent(): void {
    const label = this.take(this.newEventText);
    if (label && this.draft && !this.draft.newevents.includes(label)) {
      this.draft.newevents.push(label);
    }
    this.newEventText = '';
  }

  addHow(): void {
    const label = this.take(this.newHowText);
    if (label && this.draft && !this.draft.newhows.includes(label)) {
      this.draft.newhows.push(label);
    }
    this.newHowText = '';
  }

  addAct(): void {
    const label = this.take(this.newActText);
    if (label && this.draft && !this.draft.newacts.includes(label)) {
      this.draft.newacts.push(label);
    }
    this.newActText = '';
  }

  addResp(): void {
    const label = this.take(this.newRespText);
    if (label && this.draft && !this.draft.newresps.includes(label)) {
      this.draft.newresps.push(label);
    }
    this.newRespText = '';
  }

  addResult(eventCode: string): void {
    const label = this.take(this.newResultText[eventCode]);
    if (label && this.draft) {
      const list = (this.draft.evnew[eventCode] = this.draft.evnew[eventCode] || []);
      if (!list.includes(label)) {
        list.push(label);
      }
    }
    this.newResultText[eventCode] = '';
  }

  addQuestion(): void {
    const label = this.take(this.newQuestionLabel);
    if (label && this.draft) {
      this.draft.newQuestions.push({ label, answers: this.newQuestionAnswers });
    }
    this.newQuestionLabel = '';
    this.newQuestionAnswers = '';
  }

  remove(list: string[], index: number): void {
    list.splice(index, 1);
  }

  /** The body the portal's save expects: the same shape as its own form's post. */
  private toBody(d: CategoryDraft): Record<string, unknown> {
    const body: Record<string, unknown> = {
      name: d.name,
      subtitle: d.subtitle,
      icon: d.icon,
      colour: d.colour,
      sort_order: d.sort_order,
      events: d.events,
      evout: d.evout,
      newevents: d.newevents,
      evnew: d.evnew,
      questions: d.questions,
      newq_label: d.newQuestions.map((q) => q.label),
      newq_answers: d.newQuestions.map((q) => q.answers),
      acts_present: 1,
      acts: d.acts,
      newacts: d.newacts,
      resps_present: 1,
      resps: d.resps,
      newresps: d.newresps,
      asks_tenant: d.asks_tenant,
      asks_taken: d.asks_taken,
      asks_action: d.asks_action,
      asks_response: d.asks_response,
      tracks_violation: d.tracks_violation,
    };
    if (this.asksHow) {
      body['hows_present'] = 1;
      body['hows'] = d.hows;
      body['newhows'] = d.newhows;
    }
    return body;
  }

  save(): void {
    if (!this.draft || !this.editing || this.isSaving) {
      return;
    }
    if (!this.draft.name.trim()) {
      this._utils.showErrorInfo('', 'Give the category a name.');
      return;
    }
    this.isSaving = true;
    const body = this.toBody(this.draft);
    const request$ = this.isNew ? this.service.create(body) : this.service.save(this.editing.id, body);
    request$.subscribe({
      next: (res) => {
        this.isSaving = false;
        this._utils.showErrorSuccess('', res?.message || (this.isNew ? 'Category added.' : 'Saved.'));
        this.closePanel();
        this.load();
      },
      error: (e) => {
        this.isSaving = false;
        // The portal words its refusals to be read ("Pick at least one result for …").
        this._utils.showErrorInfo('', e?.error?.error || (this.isNew ? 'Could not add the category.' : 'Could not save the category.'));
      },
    });
  }
}
